import { KeyboardEvent, memo } from 'react';
import Image from 'next/image';
import { cn } from '@/shared/utils/cn';
import type { GameInfo } from '@/features/games/shared/types/game-info';
import { GameCardUIRating } from '@/features/games/shared/components/game-card-ui-rating';
import { absolutePathImageLoader } from '@/shared/utils/absolute-path-image-loader';

type SearchResultListItemProps = {
  game: GameInfo;
  isActive: boolean;
  onSelect: (game: GameInfo) => void;
  showPrice?: boolean;
};

export const SearchResultListItem = memo(function SearchResultListItem({
  game,
  isActive,
  onSelect,
  showPrice = false,
}: SearchResultListItemProps) {
  const { name, publisher, type, imageUrl, rating, price, currency } = game;

  const handleClick = () => {
    onSelect(game);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(game);
    }
  };

  return (
    <li
      className={cn(
        'cursor-pointer',
        'hover:bg-background-hover focus:bg-background-hover',
        isActive && 'bg-background-hover',
      )}
      role='option'
      aria-selected={isActive}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className='flex items-center gap-4 px-5 py-4'>
        <div className='bg-background-panel h-24 w-24 shrink-0 overflow-hidden rounded-xs'>
          {imageUrl ? (
            <Image
              loader={absolutePathImageLoader}
              src={imageUrl}
              alt={name}
              width={96}
              height={96}
              className='h-full w-full object-cover'
              loading='lazy'
              sizes='96px'
            />
          ) : (
            <span className='flex h-full w-full items-center justify-center text-lg font-bold text-white'>
              {name?.[0]}
            </span>
          )}
        </div>

        <div className='flex flex-1 flex-col gap-2'>
          <div className='flex items-start justify-between gap-3'>
            <span className='text-lg font-semibold'>{name}</span>
            {showPrice && price != null && (
              <span className='text-primary text-lg font-semibold whitespace-nowrap'>
                {price.toFixed(2)} {currency}
              </span>
            )}
          </div>

          <div className='flex justify-between'>
            {publisher && <span className='text-text-muted text-sm'>{publisher}</span>}
            <span
              key={type}
              className='bg-primary ml-3 rounded-sm px-3 py-1 text-xs font-semibold text-white uppercase'
            >
              {type}
            </span>
          </div>

          {rating ? (
            <GameCardUIRating value={rating} size={16} className='text-text gap-0.5 text-sm' />
          ) : null}
        </div>
      </div>
    </li>
  );
});
